import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import useIsMobile from '../hooks/useIsMobile';

function UserSettings() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    company_name: '',
    phone_number: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const glassmorphismStyle = {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    boxShadow: theme.shadows[3],
    borderRadius: 3,
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      setInitialLoading(true);
      setMessage('');
      const userId = localStorage.getItem('userToken');
      if (userId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/${userId}`);
          const data = await response.json();
          
          if (response.ok) {
            setFormData({
              username: data.username || '',
              email: data.email || '',
              company_name: data.company_name || '',
              phone_number: data.phone_number || '',
              street_address: data.address?.street || '',
              city: data.address?.city || '',
              state: data.address?.state || '',
              zip_code: data.address?.zip_code || '',
              country: data.address?.country || ''
            });
          } else {
            setMessage('Failed to load user details');
            setMessageType('error');
          }
        } catch (error) {
          setMessage('Error fetching user details');
          setMessageType('error');
        } finally {
          setInitialLoading(false);
        }
      } else {
        setMessage('User not identified. Please log in.');
        setMessageType('error');
        setInitialLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  const handleChange = (e) => {
    setMessage('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    const userId = localStorage.getItem('userToken');

    if (!userId) {
        setMessage('User not found. Please log in again.');
        setMessageType('error');
        setLoading(false);
        return;
    }

    const updateData = {
        username: formData.username,
        email: formData.email,
        company_name: formData.company_name,
        phone_number: formData.phone_number,
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country
    };

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || `HTTP error! status: ${response.status}`);
        }

        setMessage('Changes were saved successfully!');
        setMessageType('success');
        
        if (formData.company_name !== localStorage.getItem('company_name')) {
            localStorage.setItem('company_name', formData.company_name);
        }
    } catch (error) {
        setMessage(`Error updating user: ${error.message}`);
        setMessageType('error');
    } finally {
        setLoading(false);
    }
  };

  const FormSection = ({ title, icon, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <Paper
        elevation={0}
        sx={{
          ...glassmorphismStyle,
          p: { xs: 2, sm: 3 },
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon && React.cloneElement(icon, { sx: { mr: 1.5, color: 'primary.main' } })}
          <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.2) }} />
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Paper>
    </motion.div>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        px: { xs: 1, sm: 2 },
        py: 3,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: 3,
        mt: 2,
      }}
    >
      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center', color: 'text.primary' }}>
        User Settings
      </Typography>

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <FormSection title="Account Information" icon={<AccountCircleIcon />}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                type="email"
                size={isMobile ? "small" : "medium"}
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
          </FormSection>

          <FormSection title="Company Details" icon={<BusinessIcon />}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                type="tel"
                size={isMobile ? "small" : "medium"}
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
          </FormSection>

          <FormSection title="Address" icon={<LocationOnIcon />}>
            <Grid item xs={12}>
              <TextField
                label="Street Address"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="street_address"
                value={formData.street_address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="City"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="State / Province"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="ZIP / Postal Code"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                variant="outlined"
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </Grid>
          </FormSection>

          {message && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert
                severity={messageType}
                sx={{
                  mt: 2,
                  mb: 3,
                  ...glassmorphismStyle,
                  '& .MuiAlert-icon': {
                    color: `${messageType}.main`,
                  },
                  '& .MuiAlert-message': {
                     color: 'text.primary',
                  }
                }}
              >
                {message}
              </Alert>
            </motion.div>
          )}

          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || initialLoading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                py: 1.5,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </motion.div>

          <Button
            component="a"
            href="https://billing.stripe.com/p/login/test_fZe9Crcae59Q4ow4gg"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            variant="outlined"
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            Manage Subscription
          </Button>
        </Box>
      )}
    </Container>
  );
}

export default UserSettings;